import React, { useState, useEffect } from 'react';
import {
  getDailyClosingReportAPI,
  getTokenMovementAPI,
  getINRMovementAPI,
  getDollarMovementAPI,
  DailyClosingReportResponse,
  TokenMovementResponse,
  INRMovementResponse,
  DollarMovementResponse,
} from '../../../services/adminApi';
import styles from './DailyClosing.module.css';

interface SummaryCard {
  label: string;
  value: string;
  icon: string;
  color: 'blue' | 'green' | 'red';
}

interface TokenMovement {
  id: number;
  tokenName:string;
  symbol: string;
  inAmt: number;
  outAmt: number;
}

interface INRMovement {
  id: number;
  accountName:string;
  accountNo: string;
  inAmt: number;
  outAmt: number;
}

interface DollarMovement {
  id: number;
  accountName:string;
  accountNo: string;
  inAmt: number;
  outAmt: number;
}

const DailyClosing: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryCards, setSummaryCards] = useState<SummaryCard[]>([
    {
      label: 'TRANSACTIONS',
      value: '0',
      icon: '📊',
      color: 'blue',
    },
    {
      label: 'TOTAL IN (INR)',
      value: '₹0.00',
      icon: '📈',
      color: 'green',
    },
    {
      label: 'TOTAL OUT (INR)',
      value: '₹0.00',
      icon: '📉',
      color: 'red',
    },
  ]);

  const [netPosition, setNetPosition] = useState<string>('+₹0.00');
  const [tokenMovements, setTokenMovements] = useState<TokenMovement[]>([]);
  const [inrMovement, setINRMovement] = useState<INRMovement[]>([]);
  const [dollarMovement, setDollarMovement] = useState<DollarMovement[]>([]);

  const formatCurrency = (value: number): string => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchDailyClosingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Format dates to ISO 8601 DateTime format
      const fromDate = `${selectedDate}T00:00:00`;
      const toDate = `${selectedDate}T23:59:59`;
      const searchText = '';

      // Build request objects matching business API payload structure
      const tokenMovementRequest: any = {
        fromDate,
        toDate,
        searchText,
        pageNo: 1,
        pageSize: 10,
      };

      const inrMovementRequest: any = {
        fromDate,
        toDate,
        searchText,
        pageNo: 1,
        pageSize: 10,
      };

      const dollarMovementRequest: any = {
        fromDate,
        toDate,
        searchText,
        pageNo: 1,
        pageSize: 10,
      };

      // Fetch all data in parallel
      const [dailyClosingReportResponse, tokenMovementResponse, inrMovementResponse, dollarMovementResponse] = await Promise.all([
        getDailyClosingReportAPI({ fromDate, toDate }),
        getTokenMovementAPI(tokenMovementRequest),
        getINRMovementAPI(inrMovementRequest),
        getDollarMovementAPI(dollarMovementRequest),
      ]);

      // Handle Daily Closing Report
      if (dailyClosingReportResponse.isSuccess && dailyClosingReportResponse.data) {
        const report = dailyClosingReportResponse.data;
        setSummaryCards([
          {
            label: 'TRANSACTIONS',
            value: (report.totalTransaction || 0).toString(),
            icon: '📊',
            color: 'blue',
          },
          {
            label: 'TOTAL IN (INR)',
            value: formatCurrency(report.totalInAmt || 0),
            icon: '📈',
            color: 'green',
          },
          {
            label: 'TOTAL OUT (INR)',
            value: formatCurrency(report.totalOutAmt || 0),
            icon: '📉',
            color: 'red',
          },
        ]);

        // Set NET INR FLOW from totalNetAmt
        const netFlow = (report.totalNetAmt || 0) >= 0 ? '+' : '';
        setNetPosition(`${netFlow}${formatCurrency(report.totalNetAmt || 0)}`);
      }

      // Handle Token Movement
      if (tokenMovementResponse.isSuccess && tokenMovementResponse.data && Array.isArray(tokenMovementResponse.data)) {
        const tokens = tokenMovementResponse.data.map((token: TokenMovementResponse) => ({
          id:token.id,
          tokenName: token.tokenName,
          symbol: token.symbol,
          inAmt: token.inAmt,
          outAmt: token.outAmt
        }));
        setTokenMovements(tokens);
      }

      // Handle INR Movement
      if (inrMovementResponse.isSuccess && inrMovementResponse.data && Array.isArray(inrMovementResponse.data)) {
        const inrs = inrMovementResponse.data.map((inr: INRMovementResponse) => ({
          id: inr.id || 0,
          accountName: inr.accountName || '',
          accountNo: inr.accountNo || '',
          inAmt: inr.inAmt || 0,
          outAmt: inr.outAmt || 0
        }));
        setINRMovement(inrs);
      }

      // Handle Dollar Movement
      if (dollarMovementResponse.isSuccess && dollarMovementResponse.data && Array.isArray(dollarMovementResponse.data)) {
        const dollars = dollarMovementResponse.data.map((dollar: DollarMovementResponse) => ({
          id: dollar.id || 0,
          accountName: dollar.accountName || '',
          accountNo: dollar.accountNo || '',
          inAmt: dollar.inAmt || 0,
          outAmt: dollar.outAmt || 0
        }));
        setDollarMovement(dollars);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch daily closing data';
      setError(errorMessage);
      console.error('Daily closing data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Date changed to:', selectedDate);
    fetchDailyClosingData();
  }, [selectedDate]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString + 'T00:00:00');
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };
    return date.toLocaleDateString('en-IN', options);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Daily Closing</h1>
          <p className={styles.subtitle}>End of day summary by category</p>
        </div>
        <div className={styles.datePickerSection}>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className={styles.datePicker}
          />
          {loading && <span style={{ marginLeft: '10px', color: '#999' }}>Loading...</span>}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ 
          padding: '12px 16px', 
          marginBottom: '16px', 
          backgroundColor: '#fee', 
          border: '1px solid #fcc', 
          borderRadius: '4px', 
          color: '#c33' 
        }}>
          Error: {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className={styles.summaryCardsContainer}>
        {summaryCards.map((card, index) => (
          <div key={index} className={`${styles.summaryCard} ${styles[card.color]}`}>
            <div className={styles.cardContent}>
              <p className={styles.cardLabel}>{card.label}</p>
              <p className={styles.cardValue}>{card.value}</p>
            </div>
            <div className={styles.cardIcon}>{card.icon}</div>
          </div>
        ))}
      </div>

      {/* Net Position */}
      <div className={styles.netPositionSection}>
        <h2 className={styles.sectionTitle}>Net Position</h2>
        <div className={styles.netPositionCard}>
          <p className={styles.netPositionLabel}>NET INR FLOW</p>
          <p className={styles.netPositionValue}>{netPosition}</p>
        </div>
      </div>

      {/* Token Movement */}
      <div className={styles.tokenMovementSection}>
        <h2 className={styles.sectionTitle}>Token Movement</h2>
        {tokenMovements.length > 0 ? (
          <div className={styles.tokenMovementCards}>
            {tokenMovements.map((token, index) => (
              <div key={index} className={styles.tokenMovementCard}>
                {/* First Row: Symbol and Token Name */}
                <div className={styles.tokenMovementRowFirst}>
                  <span className={styles.symbolBadge}>{token.symbol}</span>
                  <p className={styles.tokenName}>
                    {token.symbol === 'ETH' ? 'Ethereum' : token.symbol === 'BTC' ? 'Bitcoin' : 'Tether'}
                  </p>
                </div>
                
                {/* Second Row: In and Out Values */}
                <div className={styles.tokenMovementRowSecond}>
                  <div className={styles.tokenMovementData}>
                    <p className={styles.tokenLabel}>In</p>
                    <p className={`${styles.tokenValue} ${styles.positive}`}>+{token.inAmt.toFixed(4)}</p>
                  </div>
                  <div className={styles.tokenMovementData}>
                    <p className={styles.tokenLabel}>Out</p>
                    <p className={`${styles.tokenValue} ${styles.negative}`}>{token.outAmt.toFixed(4)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateMessage}>No token movements for this date</p>
          </div>
        )}
      </div>

      {/* INR Movement */}
      <div className={styles.tokenMovementSection}>
        <h2 className={styles.sectionTitle}>INR Movement</h2>
        {inrMovement.length > 0 ? (
          <div className={styles.tokenMovementCards}>
            {inrMovement.map((inr, index) => (
              <div key={index} className={styles.tokenMovementCard}>
                {/* First Row: Symbol and Token Name */}
                <div className={styles.tokenMovementRowFirst}>
                  <span className={styles.symbolBadge}>{inr.accountName}</span>
                  <p className={styles.tokenName}>
                    {inr.accountNo}
                  </p>
                </div>
                
                {/* Second Row: In and Out Values */}
                <div className={styles.tokenMovementRowSecond}>
                  <div className={styles.tokenMovementData}>
                    <p className={styles.tokenLabel}>In</p>
                    <p className={`${styles.tokenValue} ${styles.positive}`}>+{inr.inAmt.toFixed(4)}</p>
                  </div>
                  <div className={styles.tokenMovementData}>
                    <p className={styles.tokenLabel}>Out</p>
                    <p className={`${styles.tokenValue} ${styles.negative}`}>{inr.outAmt.toFixed(4)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateMessage}>No INR movements for this date</p>
          </div>
        )}
      </div>

      {/* Dollar Movement */}
      <div className={styles.tokenMovementSection}>
        <h2 className={styles.sectionTitle}>Dollar Movement</h2>
       {dollarMovement.length > 0 ? (
          <div className={styles.tokenMovementCards}>
            {dollarMovement.map((dollar, index) => (
              <div key={index} className={styles.tokenMovementCard}>
                {/* First Row: Symbol and Token Name */}
                <div className={styles.tokenMovementRowFirst}>
                  <span className={styles.symbolBadge}>{dollar.accountName}</span>
                  <p className={styles.tokenName}>
                    {dollar.accountNo}
                  </p>
                </div>
                
                {/* Second Row: In and Out Values */}
                <div className={styles.tokenMovementRowSecond}>
                  <div className={styles.tokenMovementData}>
                    <p className={styles.tokenLabel}>In</p>
                    <p className={`${styles.tokenValue} ${styles.positive}`}>+{dollar.inAmt.toFixed(4)}</p>
                  </div>
                  <div className={styles.tokenMovementData}>
                    <p className={styles.tokenLabel}>Out</p>
                    <p className={`${styles.tokenValue} ${styles.negative}`}>{dollar.outAmt.toFixed(4)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateMessage}>No dollar movements for this date</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyClosing;
